/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { memo, useCallback, useMemo, useState, type MemoExoticComponent } from 'react';

import { cssMap, cx, jsx } from '@compiled/react';
import { defineMessage, useIntl } from 'react-intl';
import { graphql, useFragment } from 'react-relay';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import Badge from '@atlaskit/badge';
import InlineEdit from '@atlaskit/inline-edit';
import { Box, Text } from '@atlaskit/primitives/compiled';
import Textfield from '@atlaskit/textfield';
import { token } from '@atlaskit/tokens';
import { fireUIAnalytics } from '@atlassian/analytics-bridge';

import type { FieldBaseProps } from '../common/types';
import { useValidateRequiredField } from '../hooks/useValidateRequiredField';
import {
	DEFAULT_TIME_TRACKING_UNIT_CLIENT,
	formatTimeString,
	isValidTimeString,
	parseTimeString,
	type TimeTrackingConfig,
	toTimeFormat,
	toTimeUnit,
} from '../utils/format-time/parseTimeString';

import type { JiraTimeTrackingField_Platform$key } from './__generated__/JiraTimeTrackingField_Platform.graphql';
import { ValidationPopup } from './ValidationPopup';

/*
 * Default values copied over from @jira/src/packages/issue/field-original-estimate-readview-full/src/defaultWorkingDaysPerWeek.constants.tsx
 * Used in other implementations of the time tracking field and is the default time tracking settings values in Jira
 */
export const DEFAULT_WORKING_HOURS_PER_DAY = 8;
export const DEFAULT_WORKING_DAYS_PER_WEEK = 5;
export const DEFAULT_TIME_FORMAT = 'pretty';
export const DEFAULT_TIME_UNIT = 'minute';

/**
 * Validates and transforms an input string in the format " *w *d *h *m " (representing weeks, days, hours and minutes)
 * into seconds.
 *
 * Examples: "4d", "5h 30m", "60m", "3w"
 *
 * @param timeString - The time string to parse (e.g., "4d", "5h 30m", "60m", "3w")
 * @param config - Time tracking configuration with working hours/days
 * @returns Object with `isValid` boolean and `seconds` number (or null if invalid)
 */
export const validateAndParseTimeStringToSeconds = (
	timeString: string,
	config: TimeTrackingConfig,
): { isValid: boolean; seconds: number | null } => {
	if (!timeString || timeString.trim() === '') {
		return { isValid: true, seconds: 0 };
	}

	// Check time string is valid first
	const isValid = isValidTimeString(config)(timeString);
	if (!isValid) {
		return { isValid: false, seconds: null };
	}

	// Always parse to seconds first for easier comparison and reformatting
	// i.e. if user inputs "500h", it will be parsed to 1800000 seconds then formatted to "12w 2d 4h" (assuming default hours and days)
	const seconds = parseTimeString(config)(timeString);
	return { isValid: true, seconds };
};

const noneMessage = defineMessage({
	id: 'jira.jira.jira-platform-fields.time-tracking-field.none',
	defaultMessage: 'None',
	description: 'Display text shown when no value has been set for the field',
});

const invalidFormatMessage = defineMessage({
	id: 'jira.jira.jira-platform-fields.time-tracking-field.invalid-format-message',
	defaultMessage: 'Your estimate must be in the format 2w 4d 6h 45m',
	description: 'Validation message when invalid time format is entered',
});

const styles = cssMap({
	textFieldContainer: {
		width: '100%',
		// The TextField component has a default 8px margin top, so we are negating this here
		marginTop: token('space.negative.100'),
	},
	textFieldContainerReadView: {
		width: '100%',
	},
	textField: {
		width: '100%',
	},
	readView: {
		height: '40px',
		alignItems: 'center',
		display: 'flex',
		paddingRight: token('space.100'),
		paddingLeft: token('space.100'),
	},
	editableReadView: {
		cursor: 'pointer',
	},
});

export type Props = FieldBaseProps & {
	field: JiraTimeTrackingField_Platform$key;
	onChange: (newValue: unknown) => void;
};

const ReadView = memo(({ text, isEditable }: { isEditable?: boolean; text: string }) => {
	return (
		<Box xcss={cx(styles.readView, isEditable && styles.editableReadView)}>
			<Badge>
				<Text maxLines={1}>{text}</Text>
			</Badge>
		</Box>
	);
});

/**
 * `JiraTimeTrackingField` is a React component that renders a Jira time tracking field,
 * supporting both editable and read-only modes. It displays the field value and allows
 * users to edit the original time estimate value if the field is editable. The component uses
 * Relay fragments to fetch the current time tracking settings.
 *
 * @param field - The Relay fragment reference for the Jira time tracking field.
 * @param fieldValue - The current original time estimate value.
 * @param onChange - Callback invoked when the original time estimate value changes.
 */
export const JiraTimeTrackingField: MemoExoticComponent<
	({ fieldValue, field, isFieldEditable, onChange }: Props) => JSX.Element
> = memo(({ fieldValue, field, isFieldEditable = true, onChange }: Props): JSX.Element => {
	// eslint-disable-next-line @atlassian/relay/use-fragment-naming, @atlassian/relay/use-fragment-naming
	const data = useFragment<JiraTimeTrackingField_Platform$key>(
		graphql`
			fragment JiraTimeTrackingField_Platform on JiraTimeTrackingField {
				name
				fieldConfig {
					isEditable
					isRequired
				}
				timeTrackingSettings {
					workingHoursPerDay
					workingDaysPerWeek
					defaultFormat
					defaultUnit
				}
			}
		`,
		// eslint-disable-next-line @atlassian/relay/use-fragment-naming
		field,
	);

	const { formatMessage } = useIntl();
	const [isEditing, setIsEditing] = useState(false);

	const isEditable = !!data?.fieldConfig?.isEditable && isFieldEditable;

	const isRequired = !!data?.fieldConfig?.isRequired;

	const settingsInput: TimeTrackingConfig = useMemo(
		() => ({
			hoursPerDay: data?.timeTrackingSettings?.workingHoursPerDay ?? DEFAULT_WORKING_HOURS_PER_DAY,
			daysPerWeek: data?.timeTrackingSettings?.workingDaysPerWeek ?? DEFAULT_WORKING_DAYS_PER_WEEK,
			format: toTimeFormat(data?.timeTrackingSettings?.defaultFormat, DEFAULT_TIME_FORMAT),
			defaultUnit: toTimeUnit(data?.timeTrackingSettings?.defaultUnit, DEFAULT_TIME_UNIT),
			isTimeTrackingEnabled: true,
		}),
		[data?.timeTrackingSettings],
	);

	const getCurrentValue = (): string => {
		// Type guarding in case a string is provided
		if (typeof fieldValue === 'string') {
			return fieldValue;
		}
		if (
			typeof fieldValue === 'object' &&
			fieldValue !== null &&
			'originalEstimate' in fieldValue &&
			typeof fieldValue.originalEstimate === 'string'
		) {
			return fieldValue.originalEstimate;
		}
		// Default fallback
		return `0${DEFAULT_TIME_TRACKING_UNIT_CLIENT[settingsInput.defaultUnit]}`;
	};
	const currentValue = getCurrentValue();
	const hasValue = currentValue != null;
	const displayText = hasValue ? currentValue : formatMessage(noneMessage);

	const fieldName = data.name;
	const FIELD_TYPE_FOR_ANALYTICS = 'TextField';
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const validateRequired = useValidateRequiredField({
		isRequired,
		isEditable,
		fieldName,
		fieldTypeForAnalytics: FIELD_TYPE_FOR_ANALYTICS,
	});

	const validate = useCallback(
		(value: string) => {
			const validateError = validateRequired(value);
			if (validateError) {
				return validateError;
			}

			const result = validateAndParseTimeStringToSeconds(value, settingsInput);
			if (!result.isValid) {
				fireUIAnalytics(
					createAnalyticsEvent({
						action: 'validationError',
						actionSubject: 'inlineEdit',
					}),
					{
						fieldName: fieldName,
						fieldType: FIELD_TYPE_FOR_ANALYTICS,
						isRequired,
						isEditable,
					},
				);
				return formatMessage(invalidFormatMessage);
			}
		},
		[
			createAnalyticsEvent,
			fieldName,
			isRequired,
			isEditable,
			formatMessage,
			settingsInput,
			validateRequired,
		],
	);

	const onEdit = useCallback(() => {
		fireUIAnalytics(
			createAnalyticsEvent({
				action: 'initiated',
				actionSubject: 'inlineEdit',
			}),
			{
				fieldName,
				fieldType: FIELD_TYPE_FOR_ANALYTICS,
				isRequired,
				isEditable,
			},
		);
		setIsEditing(true);
	}, [createAnalyticsEvent, fieldName, isRequired, isEditable]);

	const onCancel = useCallback(() => {
		fireUIAnalytics(
			createAnalyticsEvent({
				action: 'cancelled',
				actionSubject: 'inlineEdit',
			}),
			{
				fieldName,
				fieldType: FIELD_TYPE_FOR_ANALYTICS,
				isRequired,
				isEditable,
			},
		);
		setIsEditing(false);
	}, [createAnalyticsEvent, fieldName, isRequired, isEditable]);

	const onConfirm = useCallback(
		(newValue: string) => {
			setIsEditing(false);

			const result = validateAndParseTimeStringToSeconds(newValue, settingsInput);
			const formattedTimeString = formatTimeString(settingsInput)(result.seconds);

			if (formattedTimeString === currentValue) {
				fireUIAnalytics(
					createAnalyticsEvent({
						action: 'cancelled',
						actionSubject: 'inlineEdit',
					}),
					{
						fieldName,
						fieldType: FIELD_TYPE_FOR_ANALYTICS,
						isRequired,
						isEditable,
					},
				);
				return;
			}

			if (result.isValid) {
				fireUIAnalytics(
					createAnalyticsEvent({
						action: 'applied',
						actionSubject: 'inlineEdit',
					}),
					{
						fieldName,
						fieldType: FIELD_TYPE_FOR_ANALYTICS,
						isRequired,
						isEditable,
					},
				);
				onChange({
					originalEstimate: formattedTimeString,
				});
			} else {
				fireUIAnalytics(
					createAnalyticsEvent({
						action: 'cancelled',
						actionSubject: 'inlineEdit',
					}),
					{
						fieldName,
						fieldType: FIELD_TYPE_FOR_ANALYTICS,
						isRequired,
						isEditable,
					},
				);
			}
		},
		[
			createAnalyticsEvent,
			fieldName,
			isRequired,
			isEditable,
			currentValue,
			settingsInput,
			onChange,
		],
	);

	const ReadViewComponent = useCallback(() => {
		return <ReadView text={currentValue} isEditable={isEditable} />;
	}, [currentValue, isEditable]);

	if (!isEditable) {
		return <ReadView text={currentValue} isEditable={isEditable} />;
	}

	return (
		<Box xcss={cx(styles.textFieldContainer, !isEditing && styles.textFieldContainerReadView)}>
			<InlineEdit
				isEditing={isEditable && isEditing}
				defaultValue={currentValue}
				editButtonLabel={displayText}
				editView={({ errorMessage, ...fieldProps }) => (
					<ValidationPopup
						errorMessage={errorMessage}
						trigger={(triggerProps) => (
							<Box xcss={styles.textField}>
								<Textfield {...fieldProps} {...triggerProps} type="text" step="any" autoFocus />
							</Box>
						)}
						placement="right"
						showInvalidPopup={fieldProps.isInvalid}
					/>
				)}
				readView={ReadViewComponent}
				onConfirm={onConfirm}
				onCancel={onCancel}
				onEdit={onEdit}
				readViewFitContainerWidth
				validate={validate}
			/>
		</Box>
	);
});
